import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { authService } from "../services/authService";
import { configService } from "../services/configService";

interface User {
  id: string;
  name?: string;
  username: string;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  role?: "superadmin" | "admin" | "staff";
  created_at?: string;
  updated_at?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (token) {
          // Set the token in the config service for API calls
          configService.setAuthToken(token);
          authService.setAuthToken(token);
          try {
            const userData = await authService.validateToken(token);
            setUser(userData);
          } catch (error) {
            // Token validation failed - clear it and continue
            localStorage.removeItem("authToken");
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        // Any unexpected error - clear token and reset state
        localStorage.removeItem("authToken");
        setUser(null);
      } finally {
        // Always set loading to false, even on errors
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const { user: userData, token } = await authService.login(
        username,
        password
      );
      localStorage.setItem("authToken", token);
      // Set the token in the config service for API calls
      configService.setAuthToken(token);
      authService.setAuthToken(token);
      setUser(userData);
      // Ensure loading is false after successful login
      setIsLoading(false);
    } catch (error) {
      // Ensure loading is false even on login error
      setIsLoading(false);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    setUser(null);
  };
  
  const refreshUser = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile);
    } catch (e) {}
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
